import React, { useEffect, useState } from "react";
import { Form, Input, Button, Row, Col, message } from "lib/ui";
import {
  Settings2,
  Coins,
  Truck,
  FileText,
  Percent,
} from "lucide-react";
import axios from "axios";
import ApiPath from "../../../hooks/ApiFetch";
import {
  AdminPageLayout,
  AdminHero,
  AdminFormPanel,
  AdminFormSection,
} from "../../../components/admin/AdminPageShell";

const { TextArea } = Input;

const SettingsPage = () => {
  axios.defaults.withCredentials = true;
  const [form] = Form.useForm();
  const [docId, setDocId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await ApiPath.get("/settings");
      const doc = Array.isArray(res.data) ? res.data[0] : res.data;
      if (doc?._id) {
        setDocId(doc._id);
        form.setFieldsValue(doc);
      }
    } catch {
      /* use defaults */
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    setSaving(true);
    try {
      if (docId) {
        await ApiPath.put(`/settings/${docId}`, values);
      } else {
        const res = await ApiPath.post("/settings", values);
        setDocId(res.data?._id);
      }
      message.success("Settings saved");
    } catch {
      message.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminPageLayout>
      <AdminHero
        icon={Settings2}
        badge="Business"
        title="Settings"
        description="Currency, delivery fees, support contacts, and legal content shown in your apps."
      />

      <AdminFormPanel
        title="Store settings"
        description="These values drive checkout, delivery quotes, and in-app legal pages."
        footer={
          <Button
            type="primary"
            htmlType="submit"
            form="settings-form"
            loading={saving}
            size="large"
          >
            Save settings
          </Button>
        }
      >
        <Form
          id="settings-form"
          form={form}
          onFinish={onFinish}
          layout="vertical"
          disabled={loading}
          className="space-y-8"
        >
          <AdminFormSection title="Currency & tax">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="Currency code" name="currency_code">
                  <Input placeholder="AED" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Currency icon" name="currency_icon">
                  <Input placeholder="د.إ" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Tax (%)" name="tax_in_percent">
                  <Input type="number" placeholder="5" />
                </Form.Item>
              </Col>
            </Row>
          </AdminFormSection>

          <AdminFormSection title="Support">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Support email" name="support_email">
                  <Input type="email" placeholder="support@tidyhub.ae" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Support phone" name="support_phone">
                  <Input placeholder="+971 …" />
                </Form.Item>
              </Col>
            </Row>
          </AdminFormSection>

          <AdminFormSection title="Delivery">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Delivery fee" name="delivery_fee">
                  <Input type="number" placeholder="0" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Fee set by" name="delivery_fee_set_by">
                  <Input placeholder="admin / zone" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Per km charge"
                  name="delivery_fee_per_km_charge"
                >
                  <Input type="number" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Distance metric" name="distance_metric">
                  <Input placeholder="km" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Max delivery distance" name="delivery_distance">
                  <Input type="number" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Referral amount" name="refer_amount">
                  <Input type="number" />
                </Form.Item>
              </Col>
            </Row>
          </AdminFormSection>

          <AdminFormSection title="Commission">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Admin commission type"
                  name="admin_commision_type"
                >
                  <Input placeholder="percent / fixed" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Admin commission value"
                  name="admin_commision_value"
                >
                  <Input type="number" />
                </Form.Item>
              </Col>
            </Row>
          </AdminFormSection>

          <AdminFormSection title="Legal content">
            <Form.Item label="Privacy policy" name="privacy_policy">
              <TextArea rows={4} placeholder="Privacy policy HTML or text" />
            </Form.Item>
            <Form.Item label="About us" name="about_us">
              <TextArea rows={4} placeholder="About us content" />
            </Form.Item>
            <Form.Item label="Terms & conditions" name="terms">
              <TextArea rows={4} placeholder="Terms content" />
            </Form.Item>
          </AdminFormSection>
        </Form>
      </AdminFormPanel>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { icon: Coins, label: "Currency", hint: "Checkout display" },
          { icon: Truck, label: "Delivery", hint: "Fees & distance" },
          { icon: FileText, label: "Legal", hint: "Policies & terms" },
          { icon: Percent, label: "Commission", hint: "Platform take" },
        ].map(({ icon: Icon, label, hint }) => (
          <div
            key={label}
            className="rounded-xl border border-slate-200/80 bg-white p-4 shadow-card"
          >
            <Icon size={20} className="text-brand" />
            <p className="mt-2 font-bold text-slate-900">{label}</p>
            <p className="text-xs text-slate-500">{hint}</p>
          </div>
        ))}
      </div>
    </AdminPageLayout>
  );
};

export default SettingsPage;
